import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { Play } from '@parrot-software/design-system/icons';
import { SubscriptionBreadcrumb } from '@subscriptions/presentation/components/base';
import { useIsSingleStoreOrganization } from '@subscriptions/presentation/hooks';
import { SUBSCRIPTION_ACADEMY_LINKS } from '@subscriptions/presentation/utils';

interface BlockDetailHeaderProps {
  actions?: ReactNode;
  name: string;
}

/**
 * BlockDetailHeader - Header with breadcrumb, title, description, and separator
 *
 * Uses the reusable SubscriptionBreadcrumb to render "Suscripción > [block name]".
 *
 * @param {Object} props - Component properties
 * @param {React.ReactNode} [props.actions] - Actions rendered on the right of the title row
 * @param {string} props.name - Block name to display in breadcrumb and title
 * @returns {React.ReactElement} Block detail header component
 *
 * @example
 * <BlockDetailHeader name="Región Centro" />
 */
export const BlockDetailHeader = ({ actions, name }: BlockDetailHeaderProps) => {
  const { t } = useTranslation('subscriptions');
  const isSingleStoreOrganization = useIsSingleStoreOrganization();

  const handlePlayClick = () => {
    window.open(SUBSCRIPTION_ACADEMY_LINKS.SUBSCRIPTION, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="px-6 py-4">
      <div className="flex flex-col gap-4">
        <SubscriptionBreadcrumb isRootLinked={!isSingleStoreOrganization} items={[{ label: name }]} />

        <div className="flex flex-col gap-3">
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-foreground">{name}</h1>
                <Button
                  aria-label="Ver tutorial"
                  className="w-6 h-6 rounded-full border-[1.5px] border-primary text-primary bg-transparent hover:bg-red-50 hover:text-primary transition-colors"
                  onClick={handlePlayClick}
                  size="icon"
                  title="Ver tutorial"
                  variant="ghost"
                >
                  <Play className="w-3! h-3!" />
                </Button>
              </div>

              <p className="text-base text-muted-foreground">{t('blockDetail.description')}</p>
            </div>

            {actions}
          </div>
        </div>
      </div>
    </div>
  );
};
